import React, { useEffect, useState } from "react";

interface LiveCcModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChannelInfo {
  hls_url: string;
  name: string;
}

const AI_API_URL = process.env.REACT_APP_AI_API_URL;

export const LiveCcModal: React.FC<LiveCcModalProps> = ({ isOpen, onClose }) => {
  const [selectedDays, setSelectedDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredBar, setHoveredBar] = useState<{ index: number; count: number; date: string } | null>(null);

  const [availableChannels, setAvailableChannels] = useState<{ hls_url: string; name: string }[]>([]);
  const [selectedChannelName, setSelectedChannelName] = useState("all");


  // Load active channels once
    useEffect(() => {
    const controller = new AbortController();
    const loadChannels = async () => {
        try {
        const res = await fetch(`${AI_API_URL}/active-channels`, { signal: controller.signal });
        if (!res.ok) throw new Error("Failed to load channels");
        const json = await res.json();
        setAvailableChannels(json.channels || []);
        } catch (e) {
        if ((e as any).name !== "AbortError") console.error("Error loading channels", e);
        }
    };
    loadChannels();
    return () => controller.abort();
    }, []);

  // Fetch real stats when modal is open / filters change
useEffect(() => {
  const controller = new AbortController();
  if (!isOpen) {
    return () => controller.abort();
  }

  setLoading(true);
  const channelParam = selectedChannelName === "all"
    ? "all"
    : encodeURIComponent(selectedChannelName);
  fetch(`${AI_API_URL}/fetch-live-cc-stats?days=${selectedDays}&channel=${channelParam}`, {
    signal: controller.signal,
  })
    .then((r) => {
      if (!r.ok) throw new Error("Live CC stats fetch failed");
      return r.json();
    })
    .then((json) => setData(json))
    .catch((err) => {
      if (err.name !== "AbortError") console.error(err);
    })
    .finally(() => setLoading(false));

  return () => controller.abort();
}, [isOpen, selectedDays, selectedChannelName, availableChannels]);


  const selectedChannelHls =
    selectedChannelName === "all"
        ? "all"
        : availableChannels.find((c) => c.name === selectedChannelName)?.hls_url || "";

  const displayChannel = selectedChannelName === "all" ? "All Channels" : selectedChannelName;
  const maxValue = data ? Math.max(...(data.per_day || []).map((item: any) => item[1])) : 0;

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
        animation: isOpen ? "fadeIn 0.3s ease-out" : "fadeOut 0.3s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "32px",
          width: "90%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          animation: isOpen ? "zoomIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)" : "",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
          <div>
            <h2 style={{ color: "#ffffff", fontSize: "24px", fontWeight: "700", margin: 0 }}>Live CC Analytics</h2>
            <p style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px", margin: "4px 0 0 0" }}>
              Closed captions activation statistics
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              color: "#ffffff",
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Controls */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
          {/* Time Period */}
          <div>
            <p style={{ margin: 0, marginBottom: 8, color: "rgba(255, 255, 255, 0.8)", fontSize: 14, fontWeight: 500 }}>
              Time Period
            </p>
            <select
              id="cc-period"
              aria-label="Time period"
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
              style={{
                display: "block",
                width: "100%",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 8,
                color: "#ffffff",
                padding: "8px 12px",
                fontSize: 14,
                outline: "none",
              }}
            >
              <option value={1} style={{ background: "#1f2937", color: "#ffffff" }}>
                Last 24 hours
              </option>
              <option value={7} style={{ background: "#1f2937", color: "#ffffff" }}>
                Last 7 days
              </option>
              <option value={14} style={{ background: "#1f2937", color: "#ffffff" }}>
                Last 14 days
              </option>
              <option value={30} style={{ background: "#1f2937", color: "#ffffff" }}>
                Last 30 days
              </option>
            </select>
          </div>

          {/* Channel Selector */}
          <div>
            <p style={{ margin: 0, marginBottom: 8, color: "rgba(255, 255, 255, 0.8)", fontSize: 14, fontWeight: 500 }}>
              Channel
            </p>
            <select
            value={selectedChannelName}
            onChange={(e) => setSelectedChannelName(e.target.value)}
            aria-label="Channel"
              style={{
                display: "block",
                width: "100%",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 8,
                color: "#ffffff",
                padding: "8px 12px",
                fontSize: 14,
                outline: "none",
              }}
            >
            <option
            value="all"
            style={{ background: "#1f2937", color: "#ffffff" }}
            >
            All Channels
            </option>

            {availableChannels.map((ch) => (
            <option
                key={ch.hls_url}
                value={ch.name}
                style={{ background: "#1f2937", color: "#ffffff" }}
            >
                {ch.name}
            </option>
            ))}
            </select>
          </div>
        </div>

        {/* Chart */}
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ color: "#ffffff", fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
            Daily CC Activations {selectedChannelHls !== "all" && `- ${displayChannel}`}
          </h3>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              padding: "24px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              height: "300px",
              display: "flex",
              position: "relative",
            }}
          >
            {loading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  color: "rgba(255, 255, 255, 0.7)",
                }}
              >
                Loading chart...
              </div>
            ) : (
              <>
                {/* Y-Axis */}
                <div
                  style={{
                    width: "50px",
                    height: "240px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    paddingRight: "12px",
                    marginRight: "16px",
                    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  {[...Array(6)].map((_, i) => {
                    const value = Math.round((maxValue * (5 - i)) / 5);
                    return (
                      <div
                        key={i}
                        style={{
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "11px",
                          fontWeight: "500",
                          textAlign: "right",
                          lineHeight: "1",
                        }}
                      >
                        {value > 0 ? value : 0}
                      </div>
                    );
                  })}
                </div>

                {/* Chart Area */}
                <div
                  style={{
                    flex: 1,
                    height: "240px",
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "space-around",
                    gap: "4px",
                    position: "relative",
                  }}
                >
                  {/* Horizontal grid lines */}
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${(i * 240) / 5}px`,
                        height: "1px",
                        background: "rgba(255, 255, 255, 0.05)",
                        pointerEvents: "none",
                      }}
                    />
                  ))}

                  {/* Bars */}
                  {data?.per_day?.map((item: any, index: number) => {
                    const [date, count] = item;
                    const height = maxValue ? (count / maxValue) * 240 : 0;

                    return (
                      <div
                        key={date}
                        style={{
                          position: "relative",
                          flex: 1,
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            height: `${height}px`,
                            width: "100%",
                            maxWidth: 40,
                            background: "linear-gradient(to top, #ef4444, #f87171)",
                            borderRadius: "4px 4px 0 0",
                            boxShadow: "0 4px 12px rgba(239,68,68,0.3)",
                            animation: `barGrowUp 1s ease-out ${index * 0.1}s both`,
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setHoveredBar({ index, count, date })}
                          onMouseLeave={() => setHoveredBar(null)}
                        />

                        {hoveredBar?.index === index && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: `${height + 10}px`,
                              left: "50%",
                              transform: "translateX(-50%)",
                              background: "rgba(0,0,0,.8)",
                              color: "#f87171",
                              padding: "4px 8px",
                              borderRadius: 4,
                              fontSize: 12,
                              fontWeight: 600,
                              pointerEvents: "none",
                            }}
                          >
                            {count}
                          </div>
                        )}

                        <div
                          style={{
                            position: "absolute",
                            bottom: -18,
                            left: "50%",
                            transform: "translateX(-50%)",
                            color: "rgba(255,255,255,.6)",
                            fontSize: 10,
                            lineHeight: 1.2,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {new Date(date).toLocaleDateString([], { month: "short", day: "numeric" })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#f87171", fontSize: "28px", fontWeight: "700", marginBottom: "4px" }}>
              {data?.total_activations?.toLocaleString() || 0}
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>Total Activations</div>
          </div>
        </div>
      </div>
    </div>
  );
};
